/**
 * Tiny localStorage-backed store with `useSyncExternalStore` semantics.
 *
 * The redesign shows two per-person surfaces the content API does not model:
 * video progress ("Continue watching") and pinned resources. Both are personal
 * preferences with no clinical value, so they live in localStorage rather than
 * behind a new set of endpoints and a Prisma migration.
 */

type Listener = () => void;

export interface LocalStore<T> {
    get(): T;
    set(next: T): void;
    subscribe(listener: Listener): () => void;
}

export function createLocalStore<T>(key: string, fallback: T): LocalStore<T> {
    const listeners = new Set<Listener>();
    let cache: T | undefined;

    const read = (): T => {
        if (cache !== undefined) return cache;
        try {
            const raw = localStorage.getItem(key);
            cache = raw ? (JSON.parse(raw) as T) : fallback;
        } catch {
            cache = fallback;
        }
        return cache;
    };

    const write = (next: T): void => {
        cache = next;
        try {
            localStorage.setItem(key, JSON.stringify(next));
        } catch {
            /* quota exceeded or storage disabled — the in-memory cache still serves this session */
        }
        listeners.forEach((listener) => listener());
    };

    // Another tab writing the same key must not leave this one showing stale
    // progress; `storage` only fires cross-tab so there is no echo loop.
    if (typeof window !== 'undefined') {
        window.addEventListener('storage', (event) => {
            if (event.key !== key) return;
            cache = undefined;
            listeners.forEach((listener) => listener());
        });
    }

    return {
        get: read,
        set: write,
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        }
    };
}
